import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Lock, Plus, Ruler, Shirt } from 'lucide-react';
import type { MeasurementTemplate } from '@tailoring-crm/shared';
import { EmptyState } from '../components/feedback/empty-state.js';
import { LoadError } from '../components/feedback/load-error.js';
import { GarmentFormDialog } from '../components/garments/garment-form-dialog.js';
import { PageHeader } from '../components/layout/page-header.js';
import { Badge, Button, ListSkeleton, useToast, cn } from '@tailoring-crm/ui';
import { useAuth } from '../lib/auth/auth-context.js';
import { formatCurrency } from '../lib/format.js';
import { useTemplates, useUpdateTemplate } from '../lib/measurements/use-templates.js';

/**
 * What the shop MAKES, and what it charges to make one.
 *
 * WHY THIS IS NOT THE PRODUCTS PAGE. They answer different questions and a shop needs both. A
 * product is a thing sold — a reel of thread, a metre of lining, a readymade piece — and has no
 * body to measure. A garment is work, and it is already the thing a deal, a quotation and an
 * order all name. Before garments carried a price, picking "Blouse" on a deal meant typing ₹800
 * every single time even though every blouse in the shop costs ₹800, because "Blouse stitching"
 * in the catalogue was a separate record with no link to the garment type.
 *
 * WHY IT IS NOT THE MEASUREMENTS SCREEN EITHER, when both edit the same record. That screen is
 * about the FIELDS a garment needs — chest, shoulder, sleeve — which is a longer job with its own
 * reordering and its own "this field is in use" rules, done a handful of times ever. This one is
 * about the name and the money, which is looked up and corrected at the counter. The link at the
 * foot of each row is the seam between them, so neither has to grow the other's controls.
 *
 * RETIRING IS THE ONLY WAY OUT, and there is deliberately no delete. A garment type is what every
 * deal, quotation and order that ever used it STORES; removing one would strand that history with
 * no name to render. Turning it off takes it out of every picker and leaves the record readable,
 * which is what a shop that has stopped making saree falls actually wants.
 */
export function GarmentsPage(): ReactElement {
  const { t } = useTranslation();
  const { role } = useAuth();

  const [includeHidden, setIncludeHidden] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<MeasurementTemplate | null>(null);

  // Every row is a selling price, so a karigar is refused the screen outright — the same refusal
  // the price list makes, and for the same reason.
  const allowed = role !== 'tailor';
  const query = useTemplates({ includeInactive: includeHidden, enabled: allowed });

  if (!allowed) {
    return (
      <>
        <PageHeader title={t('garments.title')} />
        <EmptyState
          icon={Lock}
          title={t('products.noAccessTitle')}
          description={t('products.noAccessHint')}
        />
      </>
    );
  }

  const garments = query.data?.templates ?? [];
  const hiddenCount = garments.filter((garment) => !garment.active).length;

  return (
    <>
      <PageHeader
        title={t('garments.title')}
        description={t('garments.description')}
        action={
          <Button onClick={() => setAdding(true)}>
            <Plus aria-hidden="true" />
            {t('garments.add')}
          </Button>
        }
      />

      <div className="flex flex-col gap-4">
        {/* Only offered when there is something behind it: a shop that has hidden nothing does
            not need to be told it can look at nothing. */}
        {includeHidden || hiddenCount > 0 ? (
          <label className="flex items-center gap-2 self-start text-sm">
            <input
              type="checkbox"
              className="size-4 accent-primary"
              checked={includeHidden}
              onChange={(event) => setIncludeHidden(event.target.checked)}
            />
            {t('garments.showHidden')}
          </label>
        ) : null}

        {query.isPending ? <ListSkeleton rows={5} /> : null}

        {query.isError ? <LoadError onRetry={() => void query.refetch()} /> : null}

        {query.data && garments.length === 0 ? (
          <EmptyState
            icon={Shirt}
            title={t('garments.emptyTitle')}
            description={t('garments.emptyDescription')}
            action={<Button onClick={() => setAdding(true)}>{t('garments.add')}</Button>}
          />
        ) : null}

        {garments.length > 0 ? (
          <ul className="flex flex-col gap-1.5">
            {garments.map((garment) => (
              <li key={garment.garmentTypeKey}>
                <GarmentRow garment={garment} onEdit={() => setEditing(garment)} />
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      <GarmentFormDialog open={adding} onOpenChange={setAdding} />

      {editing ? (
        <GarmentFormDialog
          open
          onOpenChange={(open) => !open && setEditing(null)}
          garment={editing}
          onSaved={() => setEditing(null)}
        />
      ) : null}
    </>
  );
}

/**
 * One garment: what it is called, what it costs, and the way through to its measurements.
 *
 * The PRICE is the thing the eye lands on, because that is what somebody opened this screen to
 * find. "Price on the piece" is written out in words rather than shown as a dash — a blank in a
 * money column reads as missing data, and this is a decision the shop made on purpose.
 */
function GarmentRow({
  garment,
  onEdit,
}: {
  garment: MeasurementTemplate;
  onEdit: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const update = useUpdateTemplate(garment.garmentTypeKey);

  const toggle = async (): Promise<void> => {
    try {
      await update.mutateAsync({ active: !garment.active });
      toast({
        title: garment.active
          ? t('garments.hidden', { name: garment.garmentTypeLabel })
          : t('garments.restored', { name: garment.garmentTypeLabel }),
        variant: 'success',
      });
    } catch {
      toast({ title: t('error.unexpected'), variant: 'destructive' });
    }
  };

  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-border p-3',
        !garment.active && 'opacity-60',
      )}
    >
      <div className="flex min-w-[10rem] flex-1 flex-col gap-0.5">
        <span className="flex items-center gap-2 text-sm font-medium">
          {garment.garmentTypeLabel}
          {!garment.active ? <Badge variant="secondary">{t('garments.hiddenBadge')}</Badge> : null}
        </span>
        <span className="text-xs text-muted-foreground">
          {t(`garments.category.${garment.category}`)}
          {garment.fields.length > 0
            ? ` · ${t('garments.fieldCount', { count: garment.fields.length })}`
            : ''}
        </span>
      </div>

      <span className="text-sm font-medium tabular-nums">
        {garment.unitPricePaise === null ? (
          <span className="text-xs font-normal text-muted-foreground">
            {t('garments.onThePiece')}
          </span>
        ) : (
          formatCurrency(garment.unitPricePaise)
        )}
      </span>

      <div className="flex items-center gap-1.5">
        <Button variant="outline" size="sm" onClick={onEdit}>
          {t('common.edit')}
        </Button>
        <Button variant="ghost" size="sm" disabled={update.isPending} onClick={() => void toggle()}>
          {garment.active ? t('garments.hide') : t('garments.restore')}
        </Button>
        {/* The seam to the other screen — see the note at the top of this file. */}
        <Button variant="ghost" size="sm" asChild>
          <Link to="/settings/measurements">
            <Ruler aria-hidden="true" className="size-4" />
            {t('garments.measurements')}
          </Link>
        </Button>
      </div>
    </div>
  );
}

export default GarmentsPage;
